/**
 * API contracts using oRPC.
 *
 * Provides contract-first type safety - contracts are defined here,
 * implemented by the API, and consumed by clients.
 *
 * This package is runtime-agnostic. Clients should use @orpc/client
 * directly to create API clients from these contracts.
 *
 * @example
 * ```typescript
 * import { createORPCClient } from "@orpc/client";
 * import { RPCLink } from "@orpc/client/fetch";
 * import { apiContract } from "@animus/api-treaty";
 *
 * const link = new RPCLink({ url: "https://api.animus.sh/rpc" });
 * const api = createORPCClient(apiContract, link);
 * ```
 */

import type { ContractRouterClient } from "@orpc/contract";

import { authContract } from "./contracts/auth";
import { authNativeContract } from "./contracts/auth-native";
import { deployContract } from "./contracts/deploy";
import { nutritionFoodsContract } from "./contracts/habits";
import { releaseContract } from "./contracts/release";
import { userContract } from "./contracts/user";

/** Full contract combining all endpoint groups */
export const apiContract = {
  auth: authContract,
  authNative: authNativeContract,
  deploy: deployContract,
  nutritionFoods: nutritionFoodsContract,
  release: releaseContract,
  user: userContract,
};

/** Type for the combined API contract */
export type ApiContract = typeof apiContract;

/** Type for a client created from the API contract */
export type ApiClient = ContractRouterClient<ApiContract>;

export {
  authContract,
  type ForgotPasswordRequest,
  ForgotPasswordRequestSchema,
  type ResetPasswordRequest,
  ResetPasswordRequestSchema,
  type Session,
  type SessionData,
  SessionDataSchema,
  SessionSchema,
  type SignInRequest,
  SignInRequestSchema,
  type SignUpRequest,
  SignUpRequestSchema,
  type SuccessResponse,
  SuccessResponseSchema,
  type WebAuthError,
  WebAuthErrorSchema,
  type WebUser,
  WebUserSchema,
} from "./contracts/auth";
export {
  type AuthError,
  AuthErrorSchema,
  type AuthorizeRequest,
  AuthorizeRequestSchema,
  type AuthorizeResponse,
  AuthorizeResponseSchema,
  type AuthUser,
  AuthUserSchema,
  authNativeContract,
  type ClientType,
  ClientTypeSchema,
  NATIVE_LOGIN_PATH,
  type NativeSession,
  NativeSessionSchema,
} from "./contracts/auth-native";
export {
  type AppName,
  AppNameSchema,
  deployContract,
  type FreezeAppRequest,
  FreezeAppRequestSchema,
  type FreezeStatus,
  FreezeStatusSchema,
  type GetFreezeResponse,
  GetFreezeResponseSchema,
  type ListFreezesResponse,
  ListFreezesResponseSchema,
  type UnfreezeAppRequest,
  UnfreezeAppRequestSchema,
} from "./contracts/deploy";
export {
  DataSourceSchema,
  EnrichmentStatusSchema,
  type FoodGroupCode,
  type ImportFoodsRequest,
  ImportFoodsRequestSchema,
  type ImportFoodsResponse,
  ImportFoodsResponseSchema,
  type ListFoodsRequest,
  ListFoodsRequestSchema,
  type ListFoodsResponse,
  ListFoodsResponseSchema,
  type NutritionFood,
  type NutritionFoodsApiError,
  type NutritionModifierSlug,
  type NutritionTag,
  nutritionFoodsContract,
  type SeedFood,
  SeedFoodSchema,
  type SeedFoodSource,
  SeedFoodSourceSchema,
} from "./contracts/habits";
export {
  type AnyRelease,
  AnyReleaseSchema,
  type CloudflareRelease,
  CloudflareReleaseSchema,
  type ListPlatformReleasesRequest,
  ListPlatformReleasesRequestSchema,
  type ListReleasesRequest,
  ListReleasesRequestSchema,
  type MobileRelease,
  MobileReleaseSchema,
  type ReleaseListResponse,
  ReleaseListResponseSchema,
  type ReleasePlatform,
  ReleasePlatformSchema,
  releaseContract,
  type SetActiveReleaseRequest,
  SetActiveReleaseRequestSchema,
  type SetActiveReleaseResponse,
  SetActiveReleaseResponseSchema,
} from "./contracts/release";
export {
  type BanUserRequest,
  BanUserRequestSchema,
  type GetUserResponse,
  GetUserResponseSchema,
  type ListUsersRequest,
  ListUsersRequestSchema,
  type ListUsersResponse,
  ListUsersResponseSchema,
  type UnbanUserRequest,
  UnbanUserRequestSchema,
  type User,
  UserSchema,
  userContract,
} from "./contracts/user";
